import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { getConfluenceDatabaseClientId } from '@atlassian/rovo-content-bridge-api-commands/clients/confluence-database-client';
import { getConfluenceSlidesClientId } from '@atlassian/rovo-content-bridge-api-commands/clients/confluence-slides-client';
import { getConfluenceEditorClientId } from '@atlassian/rovo-content-bridge-api-commands/clients/confluence-smart-create-editor-client';
import { getConfluenceWhiteboardClientId } from '@atlassian/rovo-content-bridge-api-commands/clients/confluence-whiteboard-client';
import { ROVO_CHAT_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-chat-client';
import {
	RovoSendContextReadyCommand,
	RovoStreamAbortedCommand,
	RovoStreamCompletedCommand,
	RovoStreamErroredCommand,
} from '@atlassian/rovo-content-bridge-api-commands/commands/streaming-lifecycle-commands';
import { getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api/singleton';

import { StreamLifecycleState } from './StreamLifecycleState';

/**
 * Broadcasts a stream lifecycle status to bridge API receivers.
 *
 * @param status - The lifecycle state to broadcast
 * @param streamId - The unique identifier for the message stream (tmpAssistantMessageId)
 * @param sendCommand - The bridge API sendCommand function
 * @param receiverId - The ID of the receiver (defaults to temporary ID for now)
 */
export const broadcastStreamStatus = (
	status: StreamLifecycleState,
	streamId: string,
	content?: {
		id: string | number;
		type: 'page' | 'whiteboard' | 'jira' | 'database' | 'dashboard' | 'slide';
		inlineRovoEditAttemptId?: string;
	},
): void => {
	if (!content) {
		return;
	}

	const api = getBrowserBridgeAPI();

	const id = String(content.id);

	const receiverId = (() => {
		switch (content.type) {
			case 'page':
				return getConfluenceEditorClientId(id);
			case 'database':
				return getConfluenceDatabaseClientId(id);
			case 'whiteboard':
				return getConfluenceWhiteboardClientId(id);
			case 'slide':
				return getConfluenceSlidesClientId(id);
			default:
				return undefined;
		}
	})();

	try {
		if (!receiverId) {
			return;
		}
		switch (status) {
			case StreamLifecycleState.SEND_CONTEXT_READY:
				api.send(RovoSendContextReadyCommand, {
					streamId,
					contentId: content.id,
					senderId: ROVO_CHAT_CLIENT_ID,
					receiverId,
					...(content.inlineRovoEditAttemptId && {
						inlineRovoEditAttemptId: content.inlineRovoEditAttemptId,
					}),
				});
				break;
			case StreamLifecycleState.COMPLETED:
				api.send(RovoStreamCompletedCommand, {
					streamId,
					contentId: content.id,
					senderId: ROVO_CHAT_CLIENT_ID,
					receiverId,
					...(content.inlineRovoEditAttemptId && {
						inlineRovoEditAttemptId: content.inlineRovoEditAttemptId,
					}),
				});
				break;
			case StreamLifecycleState.ERRORED:
				api.send(RovoStreamErroredCommand, {
					streamId,
					contentId: content.id,
					senderId: ROVO_CHAT_CLIENT_ID,
					receiverId,
					...(content.inlineRovoEditAttemptId && {
						inlineRovoEditAttemptId: content.inlineRovoEditAttemptId,
					}),
				});
				break;
			case StreamLifecycleState.ABORTED:
				api.send(RovoStreamAbortedCommand, {
					streamId,
					contentId: content.id,
					senderId: ROVO_CHAT_CLIENT_ID,
					receiverId,
					...(content.inlineRovoEditAttemptId && {
						inlineRovoEditAttemptId: content.inlineRovoEditAttemptId,
					}),
				});
				break;
			default:
				logException({
					exception: new Error(`Unknown stream lifecycle state: ${status}`),
					name: 'UnknownStreamLifecycleState',
				});
		}
	} catch (error: unknown) {
		logException({
			exception: error,
			name: 'SendStreamLifecycleCommandError',
		});
	}
};
